import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import {
  ScrollText,
  Filter,
  Loader2,
  User,
  Globe,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Action display config: label + color
const ACTION_CONFIG = {
  'login': { label: 'Login', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  'logout': { label: 'Logout', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  'user.create': { label: 'User Created', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'user.update': { label: 'User Updated', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'user.delete': { label: 'User Deleted', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  'user.reset_password': { label: 'Password Reset', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  'faq.create': { label: 'FAQ Created', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'faq.update': { label: 'FAQ Updated', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'faq.delete': { label: 'FAQ Deleted', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  'lead.update_status': { label: 'Lead Status Changed', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  'lead.update_notes': { label: 'Lead Notes Updated', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  'lead.delete': { label: 'Lead Deleted', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  'lead.send_email': { label: 'Email Sent', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  'inquiry.update_status': { label: 'Inquiry Status Changed', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  'inquiry.delete': { label: 'Inquiry Deleted', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  'campaign.create': { label: 'Campaign Created', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'campaign.update': { label: 'Campaign Updated', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'campaign.delete': { label: 'Campaign Deleted', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  'campaign.sync': { label: 'Campaign Synced', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  '2fa.enabled': { label: '2FA Enabled', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  '2fa.disabled': { label: '2FA Disabled', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  '2fa.verify_success': { label: '2FA Verified', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  '2fa.verify_failed': { label: '2FA Failed', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  'payment.config_updated': { label: 'Payment Config Updated', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  'payment.webhook_received': { label: 'Payment Webhook Received', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
};

const getActionMeta = (action) =>
  ACTION_CONFIG[action] || { label: action, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };

const formatMeta = (metadata) => {
  if (!metadata || Object.keys(metadata).length === 0) return '';
  return Object.entries(metadata)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
    .join(' · ');
};

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter options
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterActions, setFilterActions] = useState([]);

  // Active filters
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit-logs/filters`);
      setFilterUsers(res.data.users || []);
      setFilterActions(res.data.actions || []);
    } catch (e) {
      console.error('Error fetching audit filters:', e);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      if (userFilter) params.append('user', userFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await axios.get(`${API_URL}/api/admin/audit-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (e) {
      console.error('Error fetching audit logs:', e);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, userFilter, actionFilter, startDate, endDate]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [userFilter, actionFilter, startDate, endDate]);

  const clearFilters = () => {
    setUserFilter('');
    setActionFilter('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = userFilter || actionFilter || startDate || endDate;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="audit-logs-title">
          <ScrollText className="w-6 h-6 text-amber-400" />
          Audit Logs
        </h1>
        <p className="text-slate-400">
          A record of every sensitive admin action across the platform.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2 text-slate-400 mr-2">
            <Filter className="w-5 h-5" />
            <span className="text-sm font-medium">Filters</span>
          </div>

          {/* User filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase">User</label>
            <select
              data-testid="audit-filter-user"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 min-w-[200px]"
            >
              <option value="">All Users</option>
              {filterUsers.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          {/* Action filter */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase">Action</label>
            <select
              data-testid="audit-filter-action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 min-w-[200px]"
            >
              <option value="">All Actions</option>
              {filterActions.map((a) => (
                <option key={a} value={a}>{getActionMeta(a).label}</option>
              ))}
            </select>
          </div>

          {/* Date range */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase">From</label>
            <input
              type="date"
              data-testid="audit-filter-start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 uppercase">To</label>
            <input
              type="date"
              data-testid="audit-filter-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
            />
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              data-testid="audit-clear-filters"
              className="flex items-center gap-1 px-3 py-2 text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-lg text-sm transition-colors"
            >
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-400" data-testid="audit-total-count">
          {total} {total === 1 ? 'entry' : 'entries'}
        </p>
      </div>

      {/* Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No audit entries found</p>
            <p className="text-slate-500 text-sm mt-1">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Admin actions will appear here'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Timestamp</div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2"><User className="w-4 h-4" /> Admin User</div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">Action</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">Target</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2"><Globe className="w-4 h-4" /> IP Address</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {logs.map((log) => {
                  const meta = getActionMeta(log.action);
                  const metaStr = formatMeta(log.metadata);
                  return (
                    <tr key={log.id} className="hover:bg-slate-700/20 transition-colors" data-testid={`audit-row-${log.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-slate-300 text-sm">{new Date(log.created_at).toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white text-sm">{log.actor_email || '—'}</span>
                          {log.actor_role && (
                            <span className="text-xs text-slate-500 uppercase">{log.actor_role}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${meta.color}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col max-w-[280px]">
                          <span className="text-slate-300 text-sm truncate" title={log.target_label || ''}>
                            {log.target_type ? (
                              <>
                                <span className="text-slate-500">{log.target_type}: </span>
                                {log.target_label || log.target_id || '—'}
                              </>
                            ) : '—'}
                          </span>
                          {metaStr && (
                            <span className="text-xs text-slate-500 truncate" title={metaStr}>{metaStr}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm font-mono">{log.ip_address || '—'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && logs.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-400">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="audit-prev-page"
              className="flex items-center gap-1 px-3 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="audit-next-page"
              className="flex items-center gap-1 px-3 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAuditLogs;
